import React, { useRef, useEffect, useState } from 'react';

/**
 * EditableText - Inline editable text component
 *
 * When in edit mode, text becomes editable with a subtle highlight.
 * Supports different HTML elements (h1, h2, p, span, button).
 * Debounced onChange to prevent excessive updates.
 */
export default function EditableText({
  value,
  onChange,
  isEditing = false,
  element = 'span',
  className = '',
  placeholder = 'Click to edit...',
  multiline = false,
  debounceMs = 300
}) {
  const ref = useRef(null);
  const [localValue, setLocalValue] = useState(value);
  const debounceTimer = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Update content when value changes externally
  useEffect(() => {
    if (ref.current && !ref.current.matches(':focus')) {
      ref.current.innerText = localValue || '';
    }
  }, [localValue]);

  const handleInput = (e) => {
    const newValue = e.target.innerText;
    setLocalValue(newValue);

    // Debounce the onChange callback
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      onChange(newValue);
    }, debounceMs);
  };

  const handleBlur = (e) => {
    // Ensure final value is saved
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    onChange(e.target.innerText);
  };

  const handleKeyDown = (e) => {
    // Prevent newlines in single-line mode
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
    }

    // Allow Escape to cancel editing
    if (e.key === 'Escape') {
      ref.current.innerText = value;
      setLocalValue(value);
      ref.current?.blur();
    }
  };

  const handlePaste = (e) => {
    // Strip formatting on paste
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // If not editing, just render the text
  if (!isEditing) {
    const Tag = element;
    return (
      <Tag className={className}>
        {value || placeholder}
      </Tag>
    );
  }

  // Editing mode with contentEditable
  const Tag = element;

  const editingStyles = `
    outline-none
    ring-2 ring-blue-400/50 ring-offset-2
    rounded-sm
    cursor-text
    transition-all
    hover:ring-blue-500/70
    focus:ring-blue-500
    relative
    after:content-['✎']
    after:absolute
    after:-right-6
    after:top-0
    after:text-blue-500
    after:text-sm
    after:opacity-0
    hover:after:opacity-100
    after:transition-opacity
  `;

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`${className} ${editingStyles}`}
      onInput={handleInput}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      data-placeholder={placeholder}
      style={{
        minWidth: '50px',
        minHeight: '1em'
      }}
    >
      {localValue || ''}
    </Tag>
  );
}

/**
 * EditableButton - Special case for editable buttons
 */
export function EditableButton({
  value,
  onChange,
  isEditing = false,
  className = '',
  onClick,
  style = {}
}) {
  const ref = useRef(null);

  const handleBlur = (e) => {
    onChange(e.target.innerText);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
    }
  };

  if (!isEditing) {
    return (
      <button className={className} onClick={onClick} style={style}>
        {value}
      </button>
    );
  }

  return (
    <button
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`${className} outline-none ring-2 ring-blue-400/50 ring-offset-2 cursor-text`}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.preventDefault()} // Prevent click in edit mode
      style={style}
    >
      {value}
    </button>
  );
}
